import { motion } from 'motion/react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useIntlayer } from 'react-intlayer';

import { ImageBackground } from '../ui/image-background';

import { FadeIn } from '~/components/ui/fade-in';
import { ProductCTASection } from '~/components/ui/ProductCTASection';
import { cn } from '~/lib/utils';

interface TeamMemberCard {
  key: string;
  name: string;
  role: string;
  bio: string;
  imageSrc: string;
  objectPosition?: CSSProperties['objectPosition'];
}

interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  objectPosition?: CSSProperties['objectPosition'];
  fallbackLabel: string;
}

const BIO_REVEAL_TRANSITION = {
  duration: 0.62,
  ease: [0.34, 0.02, 0.17, 1] as const,
};

const CONTENT_FADE_TRANSITION = {
  duration: 0.3,
  ease: [0.22, 1, 0.36, 1] as const,
};

const getInitials = (value: string) => {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

const getBioParagraphs = (value: string) => {
  return value
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const ResponsiveImage = ({
  src,
  alt,
  className,
  imageClassName,
  objectPosition = 'center',
  fallbackLabel,
}: ResponsiveImageProps) => {
  const [hasError, setHasError] = useState(false);

  return (
    <div className={cn('relative overflow-hidden bg-[#090b0e]', className)}>
      {!hasError ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img
          src={src}
          alt={alt}
          className={cn(
            'absolute inset-0 h-full w-full object-cover',
            imageClassName
          )}
          style={{ objectPosition }}
          onError={() => setHasError(true)}
        />
      ) : null}

      {hasError ? (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(39,98,241,0.35),transparent_42%),linear-gradient(140deg,rgba(255,255,255,0.12),rgba(255,255,255,0.03)_35%,rgba(0,0,0,0.4)_100%)]" />
          <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:28px_28px]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="font-manrope text-[40px] font-medium leading-none text-white/90 md:text-[64px]">
              {getInitials(fallbackLabel)}
            </span>
            <span className="font-fragment-mono text-[12px] uppercase tracking-[0.12px] text-white/50 md:text-[14px] md:tracking-[0.14px]">
              {fallbackLabel}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SectionHeader = ({
  label,
  title,
}: {
  label: ReactNode;
  title: ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-3 text-white lg:gap-5">
      <FadeIn>
        <p className="font-fragment-mono text-sm uppercase tracking-[0.14px]">
          {label}
        </p>
      </FadeIn>
      <FadeIn delay={0.1}>
        <p className="font-manrope text-[24px] font-normal leading-normal tracking-[0.24px] lg:max-w-[628px] lg:text-[30px] lg:tracking-normal">
          {title}
        </p>
      </FadeIn>
    </div>
  );
};

const TeamCardToggleIcon = ({ isExpanded }: { isExpanded: boolean }) => {
  return (
    <motion.svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="block size-6"
      aria-hidden="true"
      initial={false}
    >
      <motion.line
        x1="5"
        x2="19"
        y1="12"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        animate={{ rotate: isExpanded ? 45 : 0 }}
        transition={CONTENT_FADE_TRANSITION}
        style={{ originX: '50%', originY: '50%' }}
      />
      <motion.line
        x1="12"
        x2="12"
        y1="5"
        y2="19"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        animate={{ rotate: isExpanded ? 45 : 0 }}
        transition={CONTENT_FADE_TRANSITION}
        style={{ originX: '50%', originY: '50%' }}
      />
    </motion.svg>
  );
};

const TeamMemberProfileCard = ({ member }: { member: TeamMemberCard }) => {
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [isDesktopHovered, setIsDesktopHovered] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const bioId = useId();
  const cardRef = useRef<HTMLElement>(null);
  const bioParagraphs = getBioParagraphs(member.bio);
  const isExpanded =
    isMobileExpanded || (isDesktopViewport && isDesktopHovered);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const syncViewportState = (matches: boolean) => {
      setIsDesktopViewport(matches);

      if (matches) {
        setIsMobileExpanded(false);
        return;
      }

      setIsDesktopHovered(false);
    };

    syncViewportState(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      syncViewportState(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileExpanded) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (cardRef.current?.contains(target)) {
        return;
      }

      setIsMobileExpanded(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isMobileExpanded]);

  return (
    <motion.article
      ref={cardRef}
      data-mobile-expanded={isMobileExpanded ? 'true' : 'false'}
      className="flex w-full flex-col gap-5"
      onHoverStart={() => {
        if (isDesktopViewport) {
          setIsDesktopHovered(true);
        }
      }}
      onHoverEnd={() => {
        if (isDesktopViewport) {
          setIsDesktopHovered(false);
        }
      }}
    >
      <div className="relative aspect-[410/532] w-full overflow-hidden bg-[#090b0e]">
        <ResponsiveImage
          src={member.imageSrc}
          alt={member.name}
          className="size-full"
          objectPosition={member.objectPosition}
          fallbackLabel={member.name}
        />

        <motion.div
          id={bioId}
          className="absolute inset-y-0 left-0 -right-px flex flex-col bg-graphite-dark p-5 pb-[50px] text-white will-change-transform"
          initial={false}
          animate={{ y: isExpanded ? '0%' : '104%' }}
          transition={BIO_REVEAL_TRANSITION}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-[30px] overflow-hidden">
            <div className="flex flex-col gap-2">
              <h3 className="font-manrope text-[20px] font-normal leading-[1.4] text-white">
                {member.name}
              </h3>
              <p className="font-fragment-mono text-xs uppercase tracking-[0.12px] text-graphite-700">
                {member.role}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex flex-col gap-4">
                {bioParagraphs.map((paragraph) => (
                  <p
                    key={`${member.key}-bio-${paragraph}`}
                    className="font-manrope text-sm font-normal leading-[20px] text-white lg:text-base lg:leading-[1.5] lg:tracking-[0.16px]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <span className="pointer-events-none absolute bottom-5 right-5 z-20 hidden size-10 items-center justify-center p-2 text-white lg:inline-flex">
          <TeamCardToggleIcon isExpanded={isExpanded} />
        </span>

        <button
          type="button"
          aria-controls={bioId}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Hide' : 'Show'} bio for ${member.name}`}
          className="absolute bottom-5 right-5 z-20 inline-flex size-10 items-center justify-center p-2 text-white transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:opacity-70 focus-visible:outline focus-visible:outline-1 focus-visible:outline-white"
          onClick={() => setIsMobileExpanded((current) => !current)}
        >
          <TeamCardToggleIcon isExpanded={isExpanded} />
        </button>
      </div>

      <motion.div
        className="flex flex-col gap-2"
        initial={false}
        animate={{
          opacity: isExpanded ? 0 : 1,
          y: isExpanded ? 8 : 0,
        }}
        transition={CONTENT_FADE_TRANSITION}
      >
        <h3 className="font-manrope text-[20px] font-normal leading-[1.4] text-white">
          {member.name}
        </h3>
        <p className="font-fragment-mono text-xs uppercase tracking-[0.12px] text-graphite-700">
          {member.role}
        </p>
      </motion.div>
    </motion.article>
  );
};

const TeamGrid = ({ members }: { members: TeamMemberCard[] }) => {
  return (
    <div className="grid grid-cols-1 gap-y-14 md:grid-cols-2 md:gap-x-6 md:gap-y-14 lg:grid-cols-3 lg:gap-y-12">
      {members.map((member, index) => (
        <FadeIn key={member.key} delay={0.06 * index}>
          <TeamMemberProfileCard member={member} />
        </FadeIn>
      ))}
    </div>
  );
};

export const TeamPageContent = () => {
  const { intro, mainPhrase, executive, advisory, cta } =
    useIntlayer('team-page');

  const executiveMembers: TeamMemberCard[] = [
    {
      key: 'brooks',
      name: executive.members.brooks.name.value,
      role: executive.members.brooks.role.value,
      bio: executive.members.brooks.bio.value,
      imageSrc: '/assets/images/brooks-newmark.png',
      objectPosition: 'center top',
    },
    {
      key: 'ivan',
      name: executive.members.ivan.name.value,
      role: executive.members.ivan.role.value,
      bio: executive.members.ivan.bio.value,
      imageSrc: '/assets/images/ivan-matveichenko.png',
      objectPosition: 'center top',
    },
    {
      key: 'robert',
      name: executive.members.robert.name.value,
      role: executive.members.robert.role.value,
      bio: executive.members.robert.bio.value,
      imageSrc: '/assets/images/robert-seely.png',
      objectPosition: 'center top',
    },
    {
      key: 'oleksii',
      name: executive.members.oleksii.name.value,
      role: executive.members.oleksii.role.value,
      bio: executive.members.oleksii.bio.value,
      imageSrc: '/assets/images/oleksii-tretiak.png',
      objectPosition: 'center top',
    },
    {
      key: 'viktoriia',
      name: executive.members.viktoriia.name.value,
      role: executive.members.viktoriia.role.value,
      bio: executive.members.viktoriia.bio.value,
      imageSrc: '/assets/images/viktoriia-kulchytska.png',
      objectPosition: 'center top',
    },
  ];

  const advisoryMembers: TeamMemberCard[] = [
    {
      key: 'david',
      name: advisory.members.david.name.value,
      role: advisory.members.david.role.value,
      bio: advisory.members.david.bio.value,
      imageSrc: '/assets/images/david-richards.png',
      objectPosition: 'center top',
    },
    {
      key: 'valeriy',
      name: advisory.members.valeriy.name.value,
      role: advisory.members.valeriy.role.value,
      bio: advisory.members.valeriy.bio.value,
      imageSrc: '/assets/images/valeriy-kondratyuk.png',
      objectPosition: 'center top',
    },
    {
      key: 'ralph',
      name: advisory.members.ralph.name.value,
      role: advisory.members.ralph.role.value,
      bio: advisory.members.ralph.bio.value,
      imageSrc: '/assets/images/ralph-goff.png',
      objectPosition: 'center top',
    },
    {
      key: 'glenn',
      name: advisory.members.glenn.name.value,
      role: advisory.members.glenn.role.value,
      bio: advisory.members.glenn.bio.value,
      imageSrc: '/assets/images/glenn-corn1.png',
      objectPosition: 'center top',
    },
  ];

  return (
    <div className="min-h-full w-full bg-black">
      <section className="px-4 pt-[130px] md:px-8 md:pt-[160px] lg:px-20 lg:pt-[200px]">
        <div className="flex w-full flex-col gap-[10px] lg:gap-5">
          <FadeIn>
            <p className="font-fragment-mono text-sm uppercase tracking-[0.14px] text-white">
              {intro.label}
            </p>
          </FadeIn>
          <FadeIn delay={0.12}>
            <p className="font-manrope text-[24px] font-normal leading-normal tracking-[0.24px] text-white md:max-w-[520px] lg:max-w-[628px] lg:text-[30px] lg:tracking-normal">
              {intro.description}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="relative mt-10 aspect-[375/460] w-full md:mt-12 md:aspect-auto md:h-[705px] lg:mt-[72px] lg:h-[810px]">
        <ImageBackground
          imageUrl="/assets/images/founders.jpg"
          className=""
          backgroundPosition="center top"
        />
      </section>

      <section className="px-4 py-[120px] md:px-8 md:py-[160px] lg:px-20 lg:py-[200px]">
        <div className="mx-auto max-w-[1280px] text-center text-white">
          <FadeIn>
            <h1 className="font-manrope text-[40px] font-normal leading-[1.15] md:text-[64px] md:leading-[1.2] lg:text-[96px]">
              {mainPhrase.line1}
              <br />
              {mainPhrase.line2}
            </h1>
          </FadeIn>
        </div>
      </section>

      <section className="px-4 pb-[80px] md:px-8 md:pb-[120px] lg:px-20 lg:pb-[160px]">
        <div className="flex flex-col gap-12">
          <SectionHeader label={executive.label} title={executive.title} />
          <TeamGrid members={executiveMembers} />
        </div>
      </section>

      <section className="px-4 pb-[80px] md:px-8 md:pb-[100px] lg:px-20 lg:pb-[120px]">
        <div className="flex flex-col gap-12">
          <SectionHeader label={advisory.label} title={advisory.title} />
          <TeamGrid members={advisoryMembers} />
        </div>
      </section>

      <ProductCTASection
        title={cta.label}
        heading={cta.heading}
        button1Text={cta.button}
        backgroundImage="/assets/images/chorna_cta.jpg"
        imageClassName="flip-horizontal"
      />
    </div>
  );
};
